import React, { useState, useCallback } from "react";
import { StyleSheet } from "react-native";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import { ThemedView } from "@/components/ThemedView";
import { useFirebase } from "@/components/firebase/useFirebase";
import { useAuth } from "@/components/auth/useAuth";
import { getMyShares, unshare, share } from "@/services/firebase/share-functions";
import { ShareModel } from "common247";
import { CustomAlert } from "@/components/ThemedAlert";
import { SharingCard } from "@/components/sharing/SharingCard";
import ThemedScreen from "@/components/ThemedScreen";
import { ThemedList, type ISortOption, type IGroupOption } from "@/components/ThemedList";
import { ThemedActionHoverPanel, type IBatchAction } from "@/components/ThemedActionHoverPanel";
import { CollapsibleFilterSection } from "@/components/filter";
import { useFilterPersistence } from "@/hooks/useFilterPersistence";
import { filterShares, computeFilteredCounts } from "@/services/unified-filter-service";
import { useAppDispatch, useAppSelector } from "@/services/store/hooks";
import { setShares, addOrUpdateShare, removeShare, setSharesLoading } from "@/services/store/features/sharesSlice";
import { updateContactShare, removeContactShare } from "@/services/store/features/contactsSlice";
import { entitySync } from "@/services/sync/entity-sync";

const SHARE_SORT_OPTIONS: ISortOption<ShareModel>[] = [
  { key: "sharedAt", label: "Date Shared", getValue: (s) => s.sharedAt || 0 },
  { key: "targetDisplayName", label: "Recipient Name", getValue: (s) => s.targetDisplayName || "" },
  { key: "role", label: "Permission Role", getValue: (s) => s.role },
  { key: "sourceDisplayName", label: "Item Name", getValue: (s) => s.sourceDisplayName || s.sourceId },
];

const SHARE_GROUP_OPTIONS: IGroupOption<ShareModel>[] = [
  {
    key: "sourceType",
    label: "Shared Item Type",
    getGroupValue: (s) => {
      const type = s.sourceType;
      return type.charAt(0).toUpperCase() + type.slice(1) + " Shares";
    },
  },
  {
    key: "role",
    label: "Permission Role",
    getGroupValue: (s) => (s.role === "editor" ? "Editor Permissions" : "Viewer Permissions"),
  },
];

export default function MySharesScreen() {
  const router = useRouter();
  const { sourceId, targetId, id: paramShareId } = useLocalSearchParams<{ sourceId?: string; targetId?: string; id?: string }>();
  const services = useFirebase();
  const { user } = useAuth();
  const dispatch = useAppDispatch();
  const { shares, lastFetched } = useAppSelector((state) => state.shares);
  const [loading, setLoading] = useState(shares.length === 0 && !lastFetched);
  const [refreshing, setRefreshing] = useState(false);

  // Unified filter persistence
  const { filter, setFilter, resetFilter } = useFilterPersistence("shares");

  // Selection state
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const isSelectionActive = selectionMode || selectedIds.length > 0;

  const displayedShares = React.useMemo(() => {
    if (paramShareId) {
      return shares.filter((s) => s.id === paramShareId);
    }
    if (sourceId) {
      return shares.filter((s) => s.sourceId === sourceId);
    }
    if (targetId) {
      return shares.filter((s) => s.targetId === targetId);
    }
    return shares;
  }, [shares, paramShareId, sourceId, targetId]);

  const effectiveShares = React.useMemo(() => {
    return filterShares(displayedShares, filter);
  }, [displayedShares, filter]);

  const filteredCounts = React.useMemo(() => {
    return computeFilteredCounts({ shares: displayedShares }, filter, "shares");
  }, [displayedShares, filter]);

  const fetchShares = useCallback(async (isRefresh = false) => {
    if (!services || !user) return;
    if (isRefresh || shares.length === 0) {
      setLoading(true);
    }
    dispatch(setSharesLoading(true));
    try {
      const res = await getMyShares(services, { filter });
      if (res.status) {
        dispatch(setShares(res.shares));
      } else {
        CustomAlert.alert("Error", res.error || "Failed to load shares");
      }
    } catch (error: any) {
      console.error("Fetch shares error:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      dispatch(setSharesLoading(false));
    }
  }, [services, user, dispatch, shares.length, filter]);

  useFocusEffect(
    useCallback(() => {
      fetchShares();
    }, [fetchShares])
  );

  React.useEffect(() => {
    const unsubscribe = entitySync.subscribe((event) => {
      if (event.type === "share") {
        fetchShares(false);
      }
    });
    return unsubscribe;
  }, [fetchShares]);

  const handleRoleChange = async (shareItem: ShareModel, newRole: "viewer" | "editor") => {
    if (!services) return;
    try {
      const res = await share(services, {
        sourceId: shareItem.sourceId,
        sourceType: shareItem.sourceType,
        targetId: shareItem.targetId,
        targetType: shareItem.targetType,
        role: newRole,
      });
      if (res.status) {
        const updatedShare: ShareModel = { ...shareItem, role: newRole };
        dispatch(addOrUpdateShare(updatedShare));
        if (shareItem.sourceType === "contact") {
          dispatch(updateContactShare({ contactId: shareItem.sourceId, share: updatedShare }));
        }
        entitySync.emit({
          type: "share",
          action: "update",
          id: shareItem.id || shareItem.sourceId,
          data: updatedShare,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to update share role");
      }
    } catch (error) {
      console.error("Error updating share role:", error);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!services) return;
    try {
      const res = await unshare(services, { shareId });
      if (res.status) {
        const removedShare = shares.find((s) => s.id === shareId);
        dispatch(removeShare(shareId));
        if (removedShare?.sourceType === "contact") {
          dispatch(removeContactShare({ contactId: removedShare.sourceId, shareId }));
        } else {
          dispatch(removeContactShare({ shareId }));
        }
        entitySync.emit({
          type: "share",
          action: "delete",
          id: shareId,
          data: removedShare,
        });
      } else {
        CustomAlert.alert("Error", res.error || "Failed to remove share");
      }
    } catch (error) {
      console.error("Error unsharing:", error);
    }
  };

  const toggleItemSelection = (shareItem: ShareModel) => {
    if (!shareItem.id) return;
    const isSelected = selectedIds.includes(shareItem.id);
    const newSelected = isSelected
      ? selectedIds.filter((id) => id !== shareItem.id)
      : [...selectedIds, shareItem.id];
    setSelectedIds(newSelected);
    if (newSelected.length === 0) {
      setSelectionMode(false);
    }
  };

  const handleItemPress = (shareItem: ShareModel) => {
    if (isSelectionActive) {
      toggleItemSelection(shareItem);
    } else if (shareItem.id) {
      router.push(`/(tabs)/my-shares/${shareItem.id}`);
    }
  };

  const handleItemLongPress = (shareItem: ShareModel) => {
    if (!shareItem.id) return;
    setSelectionMode(true);
    if (!selectedIds.includes(shareItem.id)) {
      setSelectedIds((prev) => [...prev, shareItem.id!]);
    }
  };

  const handleItemContextMenu = (shareItem: ShareModel, _event?: any) => {
    handleItemLongPress(shareItem);
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
    setSelectionMode(false);
  };

  const handleSelectAll = () => {
    setSelectedIds(displayedShares.map((s) => s.id!).filter(Boolean));
  };

  const handleBatchSetRole = async (newRole: "viewer" | "editor") => {
    if (!services || selectedIds.length === 0) return;
    const selectedShares = displayedShares.filter((s) => selectedIds.includes(s.id!));
    let count = 0;
    for (const s of selectedShares) {
      try {
        await handleRoleChange(s, newRole);
        count++;
      } catch (err) {
        console.error("Error setting share role:", err);
      }
    }
    CustomAlert.alert("Roles Updated", `Updated ${count} share(s) to ${newRole}.`);
    handleClearSelection();
  };

  const handleBatchRevoke = () => {
    if (!services || selectedIds.length === 0) return;
    CustomAlert.alert(
      "Revoke Shares",
      `Are you sure you want to revoke access for ${selectedIds.length} selected share(s)?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Revoke",
          style: "destructive",
          onPress: async () => {
            let revokedCount = 0;
            for (const id of selectedIds) {
              try {
                await handleUnshare(id);
                revokedCount++;
              } catch (err) {
                console.error("Error revoking share:", err);
              }
            }
            CustomAlert.alert("Revoked", `Revoked access for ${revokedCount} share(s).`);
            handleClearSelection();
          },
        },
      ]
    );
  };

  const batchActions: IBatchAction[] = [
    {
      id: "setViewer",
      label: "Set as Viewer",
      icon: "eye-outline",
      onPress: () => handleBatchSetRole("viewer"),
    },
    {
      id: "setEditor",
      label: "Set as Editor",
      icon: "pencil-outline",
      onPress: () => handleBatchSetRole("editor"),
    },
    {
      id: "revoke",
      label: "Revoke Access",
      icon: "trash-outline",
      destructive: true,
      onPress: handleBatchRevoke,
      variant: "destructive",
    },
  ];

  return (
    <ThemedScreen title="Shares" loading={loading} scrollable={false}>
      <ThemedView style={styles.container}>
        {/* Unified Collapsible Entity Filter */}
        <CollapsibleFilterSection
          screen="shares"
          filter={filter}
          onFilterChange={setFilter}
          counts={filteredCounts}
          onReset={resetFilter}
          allShares={shares}
        />
        <ThemedList<ShareModel>
          data={effectiveShares}
          sortOptions={SHARE_SORT_OPTIONS}
          defaultSortKey="sharedAt"
          defaultSortDirection="desc"
          groupOptions={SHARE_GROUP_OPTIONS}
          persistenceKey="my-shares"
          filterKeys={["targetDisplayName", "sourceDisplayName", "role", "sourceType"]}
          filterPlaceholder="Search shares..."
          loading={loading}
          onRefresh={() => {
            setRefreshing(true);
            fetchShares();
          }}
          refreshing={refreshing}
          emptyText="No shared items found. You haven't shared any memories, images, or groups yet."
          contentContainerStyle={styles.listContent}
          onItemPress={handleItemPress}
          onItemLongPress={handleItemLongPress}
          onItemContextMenu={handleItemContextMenu}
          renderItem={(item, _index, isHovered, viewMode) => {
            const dateStr = item.sharedAt ? new Date(item.sharedAt).toLocaleDateString() : "";
            const subtitle = `${item.sourceDisplayName || item.sourceId} • ${item.sourceType}${dateStr ? ` • ${dateStr}` : ""}`;
            const isSelected = selectedIds.includes(item.id!);

            return (
              <SharingCard
                share={item}
                subtitle={subtitle}
                viewMode={viewMode}
                isHovered={isHovered}
                selectionMode={isSelectionActive}
                isSelected={isSelected}
                onPress={() => handleItemPress(item)}
                onLongPress={() => handleItemLongPress(item)}
                onContextMenu={(e) => handleItemContextMenu(item, e)}
                onRoleChange={(newRole) => handleRoleChange(item, newRole)}
                onUnshare={handleUnshare}
                canShare={true}
                canUnshare={true}
              />
            );
          }}
        />

        {/* Floating Contextual Batch Action Panel */}
        <ThemedActionHoverPanel
          visible={selectedIds.length > 0}
          selectedCount={selectedIds.length}
          totalCount={displayedShares.length}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
          actions={batchActions}
        />
      </ThemedView>
    </ThemedScreen>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, padding: 8, paddingBottom: 0, minHeight: 0 },
  listContent: { paddingBottom: 40 },
});
